import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Divider, Drawer, Form, Input, InputNumber, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { PaymentMethod, SalesTransaction } from '../types/sales';
import { listSalesTransactions, createSalesTransaction } from '../services/salesService';
import masterDataService from '../services/masterDataService';
import { useResponsive } from '../hooks/useResponsive';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const paymentTags: Record<PaymentMethod, { color: string; label: string }> = {
  [PaymentMethod.CASH]: { color: 'green', label: 'Cash' },
  [PaymentMethod.CARD]: { color: 'blue', label: 'Card' },
  [PaymentMethod.BANK_TRANSFER]: { color: 'purple', label: 'Bank Transfer' },
};

function paymentTag(pm: string) {
  const key = pm as PaymentMethod;
  return paymentTags[key] ?? { color: 'default', label: pm || '—' };
}

const Sales: React.FC = () => {
  const { isMobile } = useResponsive();
  const [data, setData] = useState<SalesTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ range?: [Moment, Moment]; payment?: PaymentMethod; location?: number; search?: string }>({});
  const [open, setOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return Math.max(360, w - 200);
  });
  const [headerHeight, setHeaderHeight] = useState<number>(64);
  const [form] = Form.useForm();
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);
  const [catalogProducts, setCatalogProducts] = useState<Array<{ id: number; code: string; name: string; price: number }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [locs, prods] = await Promise.all([
          masterDataService.getLocations({ active_only: true, limit: 200 }),
          masterDataService.getProducts({ active_only: true, limit: 500 }),
        ]);
        if (cancelled) return;
        setLocations((locs || []).map((l) => ({ id: l.id, name: l.name })));
        setCatalogProducts(
          (prods || []).map((p) => ({
            id: p.id,
            code: p.code,
            name: p.name,
            price: Number(p.selling_price ?? 0),
          })),
        );
      } catch {
        if (!cancelled) {
          setLocations([]);
          setCatalogProducts([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live watchers to trigger re-renders on form changes
  const itemsWatch = Form.useWatch('items', form) || [];
  const taxWatch = Form.useWatch('tax_amount', form) || 0;
  const discountWatch = Form.useWatch('discount_amount', form) || 0;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listSalesTransactions({
        start_date: filters.range?.[0]?.format('YYYY-MM-DD'),
        end_date: filters.range?.[1]?.format('YYYY-MM-DD'),
        location_id: filters.location,
        payment_method: filters.payment,
        search: filters.search,
      });
      setData(res);
    } catch (e: any) {
      message.error(e.message || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      // leave 200px for sider on desktop; full width on small screens
      const newWidth = w <= 768 ? w : Math.max(360, w - 200);
      setDrawerWidth(newWidth);
      const hdr = document.querySelector('.ant-layout-header') as HTMLElement | null;
      if (hdr) setHeaderHeight(hdr.offsetHeight);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const columns = [
    {
      title: 'Transaction #',
      dataIndex: 'transaction_number',
      key: 'transaction_number',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'sale_date',
      key: 'sale_date',
      render: (v: string) => moment(v).format('YYYY-MM-DD'),
    },
    {
      title: 'Location',
      dataIndex: 'location_id',
      key: 'location_id',
      render: (id: number) => locations.find((l) => l.id === id)?.name || id,
    },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (pm: PaymentMethod) => {
        const t = paymentTag(String(pm));
        return <Tag color={t.color}>{t.label}</Tag>;
      },
      filters: [
        { text: 'Cash', value: PaymentMethod.CASH },
        { text: 'Card', value: PaymentMethod.CARD },
        { text: 'Bank Transfer', value: PaymentMethod.BANK_TRANSFER },
      ],
      onFilter: (value: any, record: SalesTransaction) => record.payment_method === value,
    },
    {
      title: 'Items',
      key: 'items',
      render: (_: any, record: SalesTransaction) => (
        <Text type="secondary">
          {(record.transaction_items || []).map((i) => `${i.product_name || i.product_id} x${i.quantity}`).join(', ')}
        </Text>
      ),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      align: 'right' as const,
      render: (_: any, r: SalesTransaction) => {
        const subtotal = (r.transaction_items || []).reduce((s, i) => s + (i.total_price || i.quantity * i.unit_price), 0);
        return subtotal.toFixed(2);
      },
    },
    { title: 'Tax', dataIndex: 'tax_amount', key: 'tax_amount', align: 'right' as const, render: (v: number) => v.toFixed(2) },
    { title: 'Discount', dataIndex: 'discount_amount', key: 'discount_amount', align: 'right' as const, render: (v: number) => v.toFixed(2) },
    { title: 'Total', dataIndex: 'total_amount', key: 'total_amount', align: 'right' as const, render: (v: number) => <b>{v.toFixed(2)}</b> },
  ];

  const totals = useMemo(() => {
    const subtotal = data.reduce(
      (acc, s) => acc + (s.transaction_items || []).reduce((a, i) => a + (i.total_price || 0), 0),
      0,
    );
    const tax = data.reduce((acc, s) => acc + s.tax_amount, 0);
    const discount = data.reduce((acc, s) => acc + s.discount_amount, 0);
    const total = data.reduce((acc, s) => acc + s.total_amount, 0);
    return { subtotal, tax, discount, total };
  }, [data]);

  const openNew = () => {
    if (!locations.length) {
      message.warning('No locations available. Add a location or check your permissions.');
      return;
    }
    if (!catalogProducts.length) {
      message.warning('No products loaded. Add products or check your permissions.');
      return;
    }
    const p0 = catalogProducts[0];
    setOpen(true);
    form.setFieldsValue({
      sale_date: moment(),
      location_id: locations[0].id,
      payment_method: PaymentMethod.CASH,
      items: [{ product_id: p0.id, quantity: 1, unit_price: p0.price }],
      tax_amount: 0,
      discount_amount: 0,
    });
  };

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      const sale = await createSalesTransaction({
        location_id: values.location_id,
        sale_date: values.sale_date.format('YYYY-MM-DD'),
        payment_method: values.payment_method,
        tax_amount: values.tax_amount || 0,
        discount_amount: values.discount_amount || 0,
        items: values.items.map((it: any) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
        })),
      });
      message.success(`Created ${sale.transaction_number}`);
      setOpen(false);
      form.resetFields();
      fetchData();
    } catch (e: any) {
      if (e?.errorFields) return; // form errors
      message.error(e.message || 'Failed to create sale');
    }
  };

  return (
    <div>
      <Title level={2} style={{ margin: 0, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        <ShoppingCartOutlined style={{ color: '#667eea' }} /> Sales Reports & Analytics
      </Title>

      <div>

      <Card className="sales-filters page-table-card page-table-card--green" bodyStyle={{ padding: 12 }} style={{ margin: '12px 0 16px' }}>
        <Row align="middle" justify="space-between" gutter={[12, 12]} style={{ whiteSpace: 'nowrap' }}>
          <Col flex="auto">
            <Space wrap={false}>
              <RangePicker size="large" value={filters.range as any} onChange={(v) => setFilters({ ...filters, range: v as any })} />
              <Select
                className="no-arrow"
                allowClear
                placeholder="Location"
                style={{ width: 200 }}
                size="large"
                value={filters.location}
                onChange={(v) => setFilters({ ...filters, location: v || undefined })}
                options={locations.map((l) => ({ value: l.id, label: l.name }))}
              />
              <Select
                className="no-arrow"
                allowClear
                placeholder="Payment"
                style={{ width: 200 }}
                size="large"
                value={filters.payment}
                onChange={(v) => setFilters({ ...filters, payment: v })}
                options={Object.values(PaymentMethod).map(pm => ({ value: pm, label: paymentTags[pm].label }))}
              />
              <Input.Search
                className="app-search"
                placeholder="Search transactions or items..."
                allowClear
                size="large"
                enterButton
                onSearch={(q) => setFilters({ ...filters, search: q || undefined })}
                style={{ width: 300 }}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Card 
        className="page-table-card page-table-card--emerald"
        bodyStyle={{ 
          overflow: 'visible',
          padding: 0,
          position: 'relative'
        }}
        style={{ 
          position: 'relative',
          zIndex: 1
        }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns as any}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} transactions`,
              responsive: true
            }}
            scroll={{ 
              x: isMobile ? 1200 : 1400
            }}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>
      </Card>

      <Divider />
      <Space size="large">
        <Text type="secondary">Subtotal:</Text><b>{totals.subtotal.toFixed(2)}</b>
        <Text type="secondary">Tax:</Text><b>{totals.tax.toFixed(2)}</b>
        <Text type="secondary">Discount:</Text><b>{totals.discount.toFixed(2)}</b>
        <Text>Total:</Text><b>{totals.total.toFixed(2)}</b>
      </Space>

      <Drawer
        placement="right"
        open={open}
        width={drawerWidth}
        onClose={() => { setOpen(false); form.resetFields(); }}
        style={{ top: headerHeight }}
        maskStyle={{ top: headerHeight }}
        headerStyle={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff'
        }}
        title={<div style={{ color: '#fff', fontWeight: 'bold' }}><ShoppingCartOutlined /> Create Sale</div>}
        bodyStyle={{ maxHeight: `calc(100dvh - ${headerHeight + 56}px)`, overflowY: 'auto', padding: '24px' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setOpen(false); form.resetFields(); }}>❌ Cancel</Button>
            <Button type="primary" onClick={onCreate} style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}>✅ Create Sale</Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="sales-form" autoComplete="off">
          <Divider orientation="left" style={{ fontSize: 16, fontWeight: 'bold', color: '#667eea' }}>🧾 Sale Details</Divider>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={8}>
              <Form.Item name="sale_date" label={<span style={{ fontWeight: 600 }}>Date</span>} rules={[{ required: true }]}>
                <DatePicker size="large" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="location_id" label={<span style={{ fontWeight: 600 }}>Location</span>} rules={[{ required: true }]}>
                <Select className="no-arrow" size="large" options={locations.map((l) => ({ value: l.id, label: l.name }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="payment_method" label={<span style={{ fontWeight: 600 }}>Payment</span>} rules={[{ required: true }]}>
                <Select className="no-arrow" size="large" options={Object.values(PaymentMethod).map(pm => ({ value: pm, label: paymentTags[pm].label }))} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 16, fontWeight: 'bold', color: '#667eea' }}>🧩 Items</Divider>
          <Form.List name="items" rules={[{ validator: async (_, items) => { if (!items || !items.length) return Promise.reject(new Error('Add at least one item')); } }]}>
            {(fields, { add, remove }) => (
              <>
                {/* header row */}
                <Row gutter={12} style={{ fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                  <Col xs={24} md={12}>Product</Col>
                  <Col xs={12} md={4}>Qty</Col>
                  <Col xs={12} md={6}>Price</Col>
                  <Col xs={12} md={2} style={{ textAlign: 'right' }}>Total</Col>
                  <Col xs={12} md={0}></Col>
                </Row>
                {fields.map((field) => (
                  <Row key={field.key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col xs={24} md={12}>
                      <Form.Item {...field} name={[field.name, 'product_id']} rules={[{ required: true }]}> 
                        <Select
                          className="no-arrow"
                          size="large"
                          showSearch
                          filterOption={(input, opt) => (opt?.label as string).toLowerCase().includes(input.toLowerCase())}
                          options={catalogProducts.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
                          onChange={(val) => {
                            const prod = catalogProducts.find((p) => p.id === val);
                            if (prod) {
                              const current = form.getFieldValue('items') || [];
                              current[field.name] = {
                                ...(current[field.name] || {}),
                                product_id: val,
                                unit_price: current[field.name]?.unit_price ?? prod.price,
                                quantity: current[field.name]?.quantity ?? 1,
                              };
                              form.setFieldsValue({ items: current });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Item {...field} name={[field.name, 'quantity']} rules={[{ required: true, type: 'number', min: 1 }]}>
                        <InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item {...field} name={[field.name, 'unit_price']} rules={[{ required: true, type: 'number', min: 0 }]}>
                        <InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} step={0.01} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={2} style={{ textAlign: 'right' }}>
                      <Text strong>
                        {(() => {
                          const it = itemsWatch?.[field.name] || {};
                          const total = Number(it.quantity || 0) * Number(it.unit_price || 0);
                          return total.toFixed(2);
                        })()}
                      </Text>
                    </Col>
                    <Col xs={12} md={0} style={{ textAlign: 'right' }}>
                      <Button danger onClick={() => remove(field.name)}>Remove</Button>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ quantity: 1 })} style={{ width: '100%' }}>+ Add item</Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left" style={{ fontSize: 16, fontWeight: 'bold', color: '#667eea' }}>📋 Summary</Divider>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={8}>
              <Form.Item name="tax_amount" label={<span style={{ fontWeight: 600 }}>Tax</span>}>
                <InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="discount_amount" label={<span style={{ fontWeight: 600 }}>Discount</span>}>
                <InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<span style={{ fontWeight: 600 }}>Total</span>}>
                <Text strong>
                  {(() => {
                    const subtotal = (itemsWatch as any[]).reduce((s: number, it: any) => s + (Number(it?.quantity || 0) * Number(it?.unit_price || 0)), 0);
                    const tax = Number(taxWatch || 0);
                    const discount = Number(discountWatch || 0);
                    return (subtotal + tax - discount).toFixed(2);
                  })()}
                </Text>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
      </div>
    </div>
  );
};

export default Sales;
